<template>
    <FormsCard>
        <router-link to="/auth" class="auth-back-link">
            <font-awesome-icon icon="fa-arrow-left"/>
            {{ $t('SuccessRegistrationComponent.backToLogin') }}
        </router-link>
        <img class="block mx-auto mb-6 w-32 sm:w-40 h-auto" src="@/assets/omind_logo.png" alt="test">
        <h2 class="text-center pb-5 text-xl sm:text-2xl">
            {{ $t('RegisterComponent.createAccount') }}</h2>

        <!-- Display form-level errors -->
        <!--div v-if="formErrors.length > 0" class="mb-4 p-3 bg-red-50
           border border-red-200 rounded-lg">
            <div v-for="error in formErrors" :key="error" class="text-red-600 text-sm">
                {{ error }}
            </div>
        </div-->

        <Form :validateOnValueUpdate="false" :resolver="resolver"
            :validateOnBlur="true" @submit="saveRegisterData" @invalid="handleFormInvalid"
            class="flex flex-col gap-4 w-full md:shrink-0">
            <FormField v-slot="$field" as="section" name="firstName"
                initialValue="" class="flex flex-col gap-1 ">
                <InputText type="text" size="large" :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.firstName')"
                    data-testid="txt_firstName"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="lastName"
                initialValue="" class="flex flex-col gap-1 ">
                <InputText type="text" size="large" :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.lastName')"
                    data-testid="txt_lastName"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="email"
                initialValue="" class="flex flex-col gap-1 ">
                <InputText type="text" size="large" :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.email')"
                    data-testid="txt_email"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="password"
                initialValue="" class="flex flex-col gap-1">
                <Password type="text" :feedback="false" fluid size="large"
                    :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.password')"
                    data-testid="txt_password"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="repeatPassword"
                initialValue="" class="flex flex-col gap-1">
                <Password type="text" :feedback="false" fluid size="large"
                    :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.repeatPassword')"
                    data-testid="txt_repeatPassword"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-if="!skipActivation" v-slot="$field" as="section" name="mobileNumber"
                initialValue="" class="flex flex-col gap-1 ">
                <InputText type="text" size="large" :invalid="$field?.invalid"
                    :placeholder="$t('RegisterComponent.placeholders.mobileNumber')"
                    data-testid="txt_mobileNumber"
                    @input="onFieldChange"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="timezone"
                :initialValue="defaultTimezone" class="flex flex-col gap-1">
                <label class="text-base text-surface-600" for="sel_timezone">
                    {{ $t('RegisterComponent.timezone') }}
                </label>
                <Select
                    v-model="$field.value"
                    :options="timezoneOptions"
                    filter
                    :placeholder="$t('RegisterComponent.placeholders.timezone')"
                    :invalid="$field?.invalid"
                    class="w-full"
                    data-testid="sel_timezone"
                    @change="onFieldChange"
                />
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <div class="flex flex-wrap">
                <div class="flex items-center gap-2">
                    <FormField v-slot="$field" as="section" name="tos"
                initialValue="no" class="flex flex-row gap-1 items-center">
                    <RadioButton v-model="$field.value" :invalid="$field?.invalid"
                        id="rd_terms-of-use" value="yes" @change="onFieldChange" />
                    <label for="rd_terms-of-use" class="text-lg">
                        <i18n-t keypath="RegisterComponent.termsOfUseAndPrivacy">
                            <router-link class="text-[var(--color-orange)] text-lg font-bold py-2
                                px-2 transition duration-500 hover:text-[var(--color-fulvous)]"
                                to="/privacy" target="_blank">
                                {{ $t('RegisterComponent.privacyPolicy') }}
                            </router-link>
                        </i18n-t>
                    </label>
                    <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
                </FormField>
                </div>
            </div>
            <div class="flex flex-row">
                <LanguageComponent class="float-left mt-1 absolute"/>
            </div>
            <Button type="submit" :disabled="isSubmitting || formErrors.length > 0"
                class="btn-primary-accent mt-8 sm:mt-12 w-full sm:w-auto px-6 py-2.5 sm:px-12 sm:py-3 text-lg sm:text-2xl mx-auto block text-center" unstyled
                data-testid="btn_register">
                {{ isSubmitting ? $t('RegisterComponent.register') + '...' :
                 $t('RegisterComponent.register') }}
            </Button>
        </Form>
    </FormsCard>
</template>

<script>
import LanguageComponent from '@/components/global/LanguageComponent.vue';
import FormsCard from '../global/slots/FormsCard.vue';
import { Form, FormField } from '@primevue/forms';
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import Password from 'primevue/password';
import Button from 'primevue/button';
import RadioButton from 'primevue/radiobutton';
import Select from 'primevue/select';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import {
  DEFAULT_USER_TIMEZONE,
  getBrowserTimezone,
  getTimezoneOptions,
} from '@/helpers/timezoneOptions';

export default {
  name: 'RegisterComponent',
  components: {
    LanguageComponent,
    FormsCard,
    Form,
    FormField,
    InputText,
    Message,
    Password,
    Button,
    RadioButton,
    Select,
  },
  data() {
    return {
      isSubmitting: false,
      formErrors: [],
      timezoneOptions: getTimezoneOptions(),
      defaultTimezone: getBrowserTimezone(),
    };
  },
  methods: {
    // Reset form state after errors
    resetFormState() {
      this.isSubmitting = false;
      this.formErrors = [];
      // Reset store validation state
      this.$store.commit('setRegisterDataValid', false);
      this.$store.commit('setRegisterData', null);
    },

    // Handle form field changes to enable editing after errors
    onFieldChange() {
      // If there were form errors, clear them when user starts typing
      if (this.formErrors.length > 0) {
        this.formErrors = [];
      }
    },

    // Handle form validation failures
    handleFormInvalid(event) {
      console.log('Form validation failed:', event);
      this.formErrors = [this.$t('RegisterComponent.validation.pleaseCheckInput')];
      this.$toast.add({
        severity: 'warn',
        summary: this.$t('RegisterComponent.validation.formValidationFailed'),
        detail: this.$t('RegisterComponent.validation.pleaseCheckInput'),
        life: 5000,
      });
    },
    async createAccount(values) {
      try {
        const registerData = {
          ...this.$store.state.auth.registerData,
          preferredLanguage: this.lang,
          timezone: values.timezone || this.defaultTimezone || DEFAULT_USER_TIMEZONE,
        };
        await this.$store.dispatch('registerUser', registerData);
        this.isSubmitting = false;
        if (import.meta.env.VITE_SKIP_ACTIVATION === 'true') {
          await this.$store.dispatch('login', {
            email: values.email,
            password: values.password,
          });
          if (this.$store.getters.isLoggedIn) {
            this.$router.push('/');
          }
        } else this.$router.push('/auth/register-success');
      } catch (error) {
        // Handle different types of errors gracefully
        let errorMessage = this.$t('RegisterComponent.validation.accountCreationError');

        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data.message)) {
            errorMessage = this.$t('RegisterComponent.validation.pleaseCheckInput');
          } else {
            errorMessage = error.response.data.message || errorMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.resetFormState();
        this.$toast.add({
          severity: 'error',
          summary: errorMessage,
          detail: this.$t('RegisterComponent.validation.tryAgainOrContactSupport'),
          life: 5000,
        });
        // Ensure button is enabled after error
        this.formErrors = [];
      }
    },
    async saveRegisterData(values) {
      // Debug: Log the values parameter structure
      console.log('saveRegisterData called with:', values);
      console.log('values type:', typeof values);
      console.log('values keys:', Object.keys(values || {}));

      // Handle different possible parameter structures from PrimeVue Forms
      let formValues = {};
      let isValid = false;

      if (values && typeof values === 'object') {
        // Try different possible structures
        if (values.values && values.valid !== undefined) {
          // Structure: { values: {...}, valid: boolean }
          formValues = values.values;
          isValid = values.valid;
        } else if (values.valid !== undefined) {
          // Structure: { valid: boolean, ...formData }
          const { valid, ...rest } = values;
          formValues = rest;
          isValid = valid;
        } else if (Object.keys(values).length > 0) {
          // Structure: direct form data
          formValues = values;
          isValid = true; // Assume valid if no explicit valid flag
        }
      }

      console.log('Extracted formValues:', formValues);
      console.log('Extracted isValid:', isValid);

      // For now, let's be more lenient and allow the form to proceed
      // even if we can't determine validity - the backend will validate
      if (!isValid && values && Object.keys(values).length > 0) {
        console.warn('Form validity unclear, but proceeding with submission');
        isValid = true;
        formValues = values; // Use the original values
      }

      // Check if form is valid before proceeding
      if (!isValid) {
        this.$toast.add({
          severity: 'warn',
          summary: this.$t('RegisterComponent.validation.formValidationFailed'),
          detail: this.$t('RegisterComponent.validation.pleaseCheckInput'),
          life: 5000,
        });
        return;
      }

      try {
        this.isSubmitting = true;

        if (!formValues || Object.keys(formValues).length === 0) {
          throw new Error('No form values received');
        }

        const {
          tos,
          repeatPassword,
          ...rest
        } = formValues;

        await this.$store.dispatch('validateRegister', rest);
        if (this.dataValid) {
          await this.createAccount(formValues);
        } else {
          // If validation failed, reset form state and show message
          this.resetFormState();
          this.formErrors = [this.$t('RegisterComponent.validation.pleaseCheckInput')];
          this.$toast.add({
            severity: 'warn',
            summary: this.$t('RegisterComponent.validation.validationFailed'),
            detail: this.$t('RegisterComponent.validation.pleaseCheckInput'),
            life: 5000,
          });
        }
      } catch (error) {
        // Handle different types of errors gracefully
        let errorMessage = this.$t('RegisterComponent.validation.registrationError');
        let errorCode = null;

        if (error.response && error.response.data) {
          if (Array.isArray(error.response.data.message)) {
            errorMessage = this.$t('RegisterComponent.validation.pleaseCheckInput');
          } else {
            errorMessage = error.response.data.message || errorMessage;
          }
          errorCode = error.response.data.code;
        } else if (error.message) {
          errorMessage = error.message;
        }

        let toastErrorMessage;
        switch (errorCode) {
          case 40003:
            toastErrorMessage = this.$t(
              'RegisterComponent.toast.userAlreadyExist',
              { param_email: formValues.email || 'unknown' },
            );
            break;
          case 40006:
            toastErrorMessage = this.$t(
              'RegisterComponent.toast.invalidUser',
              { param_email: formValues.email || 'unknown' },
            );
            break;
          default:
            toastErrorMessage = errorMessage;
        }

        // Reset submission state and show error
        this.resetFormState();
        this.formErrors = [toastErrorMessage];
        this.$toast.add({
          severity: 'error',
          summary: toastErrorMessage,
          detail: this.$t('RegisterComponent.validation.pleaseCheckInput'),
          life: 5000,
        });
        // Clear form errors after a short delay to re-enable the button
        setTimeout(() => {
          this.formErrors = [];
        }, 100);
      }
    },
  },
  computed: {
    dataValid() {
      return this.$store.state.auth.registerDataValid;
    },
    lang() {
      return this.$i18n.locale;
    },
    skipActivation() {
      return import.meta.env.VITE_SKIP_ACTIVATION === 'true';
    },
    // Dynamic resolver that reacts to language changes
    resolver() {
      return zodResolver(
        z.object({
          firstName: z.string()
            .min(2, this.$t('RegisterComponent.validation.firstNameMin'))
            .max(100, this.$t('RegisterComponent.validation.firstNameMax'))
            .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'.-]+$/, this.$t('RegisterComponent.validation.firstNameFormat')),
          lastName: z.string()
            .min(2, this.$t('RegisterComponent.validation.lastNameMin'))
            .max(100, this.$t('RegisterComponent.validation.lastNameMax'))
            .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'.-]+$/, this.$t('RegisterComponent.validation.lastNameFormat')),
          email: z.string()
            .min(1, this.$t('RegisterComponent.validation.emailRequired'))
            .email(this.$t('RegisterComponent.validation.emailFormat')),
          password: z.string()
            .min(8, this.$t('RegisterComponent.validation.passwordMin'))
            .max(100, this.$t('RegisterComponent.validation.passwordMax')),
          repeatPassword: z.string()
            .min(8, this.$t('RegisterComponent.validation.repeatPasswordMin'))
            .max(100, this.$t('RegisterComponent.validation.repeatPasswordMax')),
          mobileNumber: z.string()
            .optional()
            .or(z.literal('')) // Accepts empty strings
            .refine((value) => {
              // Allow null/empty values
              if (value === null || value === '') {
                return true;
              }

              // Remove all spaces and dashes for validation
              const cleanNumber = value.replace(/[\s-]/g, '');

              // Check if it starts with + or 00
              if (cleanNumber.startsWith('+')) {
                // Format: +[country code][number]
                const withoutPlus = cleanNumber.substring(1);
                if (!/^[1-9]\d{0,3}\d{7,11}$/.test(withoutPlus)) {
                  return false;
                }
              } else if (cleanNumber.startsWith('00')) {
                // Format: 00[country code][number]
                const withoutZeros = cleanNumber.substring(2);
                if (!/^[1-9]\d{0,3}\d{7,11}$/.test(withoutZeros)) {
                  return false;
                }
              } else if (!/^\d{7,15}$/.test(cleanNumber)) {
                // Format: just the number
                return false;
              }

              // Additional validation: ensure reasonable length
              if (cleanNumber.length < 7 || cleanNumber.length > 15) {
                return false;
              }

              return true;
            }, {
              message: this.$t('RegisterComponent.validation.mobileNumberFormat'),
            }),
          timezone: z.string()
            .min(1, this.$t('RegisterComponent.validation.timezoneRequired'))
            .max(100, this.$t('RegisterComponent.validation.timezoneMax')),
          tos: z.string().refine((val) => val === 'yes', {
            message: this.$t('RegisterComponent.validation.tosRequired'),
          }),
        }).superRefine(({ password, repeatPassword }, ctx) => {
          if (password !== repeatPassword) {
            ctx.addIssue({
              path: ['repeatPassword'],
              message: this.$t('RegisterComponent.validation.passwordsMismatch'),
            });
          }
        }),
      );
    },
  },
  watch: {
    lang() {
      // Safely handle language change focus
      try {
        const firstNameEl = document.getElementById('txt_first-name');
        if (firstNameEl && typeof firstNameEl.focus === 'function') {
          firstNameEl.focus();
          firstNameEl.blur();
        }
      } catch (error) {
        // Silently ignore focus errors - they shouldn't break the form
        console.warn('Could not focus on first name field during language change:', error);
      }
    },
  },
  mounted() {
    this.initData = this.$store.state.auth.registerData ?? { tos: 0 };
  },
};
</script>
